/**
 * React port of Angular `TransferProcessComponent`
 * (`frontend-app/src/app/modules/transfer-process/transfer-process.component.ts`).
 */
import { Loader2 } from "lucide-react"
import { PageHeader } from "@/features/invitee/components/PageHeader"
import { EntityEditTabBar } from "@/components/ui/EntityEditTabBar"
import type { TransferTabId } from "../types"
import { useTransferProcessPage } from "../hooks/useTransferProcessPage"
import { TransferProcessPageState } from "../components/TransferProcessPageState"
import { TransferProcessEntityHeader } from "../components/TransferProcessEntityHeader"
import { TransferProcessWizardFooter } from "../components/TransferProcessWizardFooter"
import { TransferProcessDialogs } from "../components/TransferProcessDialogs"
import { TransferProcessHiddenFields } from "../components/TransferProcessHiddenFields"
import { TransferPropertyInfoTab } from "../components/tabs/TransferPropertyInfoTab"
import { TransferContactsTab } from "../components/tabs/TransferContactsTab"
import { TransferResidentsTab } from "../components/tabs/TransferResidentsTab"
import { TransferSummaryTab } from "../components/tabs/TransferSummaryTab"

export function TransferProcessPage() {
  const ctx = useTransferProcessPage()
  const {
    fragment,
    pageHeaderTitle,
    entityId,
    entityLoadStatus,
    navigate,
    processing,
    selectedTab,
    setSelectedTab,
    transferEntityTabs,
  } = ctx

  const pageState = TransferProcessPageState({
    fragment,
    pageHeaderTitle,
    entityId,
    entityLoadStatus,
    navigate,
  })
  if (pageState) return pageState

  return (
    <div
      data-testid="transfer-process-page"
      className="min-h-screen min-w-0 w-full bg-[#f4f7f6] pb-12 font-sans text-slate-900"
    >
      <PageHeader title={pageHeaderTitle} testIdRoot="transfer-process-page-header" />
      <div className="mx-auto flex w-full min-w-0 flex-col gap-6 px-3 py-6 pb-12 sm:px-6">
        <div
          data-testid="transfer-process-main-card"
          className="min-w-0 overflow-visible rounded-xl border border-slate-200/90 bg-white shadow-[0_5px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-100"
        >
          <div className="min-w-0 p-4 sm:p-6">
            {processing && (
              <div
                data-testid="transfer-process-processing-banner"
                className="mb-4 flex items-center gap-2 text-sm text-[#778aa4]"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            )}

            <TransferProcessEntityHeader ctx={ctx} />

            <TransferProcessHiddenFields ctx={ctx} />

            <div
              data-testid="transfer-process-content-layout"
              className="flex min-w-0 w-full flex-col"
            >
              <div className="-mx-4 min-w-0 sm:-mx-6">
                <EntityEditTabBar
                  navTestId="transfer-process-step-nav"
                  tabs={transferEntityTabs}
                  activeTab={selectedTab}
                  onSelect={(id) => setSelectedTab(id as TransferTabId)}
                />
              </div>

              <div
                data-testid="transfer-process-tab-panel"
                className="min-w-0 flex-1 -mx-4 bg-slate-50/50 px-4 py-4 sm:-mx-6 sm:px-6 sm:py-6"
              >
                <TransferPropertyInfoTab ctx={ctx} />
                <TransferContactsTab ctx={ctx} />
                <TransferResidentsTab ctx={ctx} />
                <TransferSummaryTab ctx={ctx} />
                <TransferProcessWizardFooter ctx={ctx} />
              </div>
            </div>

            <TransferProcessDialogs ctx={ctx} />
          </div>
        </div>
      </div>
    </div>
  )
}
